import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuPanel } from '@angular/material/menu';
import { DialogClosingParams } from '@nibras/core';
import { BaseComponentOnPush } from '@nibras/shared/components';
import { Subject, takeUntil } from 'rxjs';

export abstract class NibrasDialogContentComponent extends BaseComponentOnPush {}

@Component({
  selector: 'nibras-dialog',
  templateUrl: 'nibras-dialog.component.html',
  encapsulation: ViewEncapsulation.None
})
export class NibrasDialogComponent implements OnInit {
  @Input() title = '';
  @Input() headerText = '';
  @Input() showFooter = true;
  @Input() enableMaximize = false;
  @Input() hideCloseIcon = false;
  @Input() showHeader = true;
  @Input() dialogRef: MatDialogRef<HTMLElement>;
  @Input() closingParams: DialogClosingParams;
  @Input() settingsMenuItems: MatMenuPanel;

  @Output() onMaximized: EventEmitter<boolean> = new EventEmitter();

  @Output()
  onContentComponentCreated: EventEmitter<NibrasDialogContentComponent> = new EventEmitter();

  @ContentChild('nibrasDialogContent')
  contentComponent: NibrasDialogContentComponent;
  public isMaximised = false;
  resizeSubject = new Subject<DOMRect>();
  private destroy$ = new Subject<void>();

  constructor(
    @Optional() dialogRef: MatDialogRef<HTMLElement>,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) {
    this.dialogRef = dialogRef;
  }

  ngAfterViewInit() {
    if (!this.dialogRef) {
      this.dialogRef = getClosestDialog(this.elementRef, this.dialog.openDialogs);
    }
  }

  ngAfterContentInit() {
    if (this.contentComponent) {
      this.onContentComponentCreated.emit(this.contentComponent);
    }
  }

  ngOnInit() {
    this.disableKeyDownEvent();
    this.listenForDialogResize();
  }

  private disableKeyDownEvent() {
    //Temp work arond to fix angular's memory leak issue.
    //Later need to upraged to latest angular version
    //https://github.com/angular/components/pull/23962/commits/1b84a9df23d4448af4e00b9fbdc75f0cf21de681
    const keyDownSubject: Subject<any> =
      (this.dialogRef as any)?._overlayRef?._keydownEvents || null;
    if (this.dialogRef?.disableClose && !keyDownSubject?.closed) {
      keyDownSubject?.complete();
    }
  }

  /** Maximize action not handling here, as the dialog ref used bunch of area - if any component required this functionality
   * make 'enableMaximize' to true and listen for maximise method and apply the custom css override.
   *
   * @description
   * @author Sinosar Thenutty Kallingal
   * @date 15/09/2021
   * @memberof NibrasDialogComponent
   */
  maximise() {
    const _clsName = 'dialog-maximised';
    if (this.isMaximised) {
      this.dialogRef.removePanelClass(_clsName);
    } else {
      this.dialogRef.addPanelClass(_clsName);
    }
    this.isMaximised = !this.isMaximised;

    //trigger event after the resize animation
    setTimeout(() => this.onMaximized.emit(this.isMaximised), 200);
  }

  listenForDialogResize() {
    const containerElement: HTMLElement = (this.dialogRef?._containerInstance as any)?._elementRef
      ?.nativeElement;
    const overlayElement: HTMLElement = (this.dialogRef as any)?._overlayRef?.overlayElement;
    if (!containerElement || !overlayElement?.classList?.contains('resizable')) return;
    const ResizeObserver = (window as any).ResizeObserver;
    const observer = new ResizeObserver((entries: any) => {
      if (entries?.length && entries[0]?.contentRect) {
        this.resizeSubject.next(entries[0]?.contentRect);
      }
    });

    observer.observe(containerElement);

    this.resizeSubject?.pipe(takeUntil(this.destroy$)).subscribe(s => {
      // overlayElement.style.minHeight = `${height}px`;
      overlayElement.style.minWidth = 'auto';
    });
  }

  closeDialog() {
    this.dialogRef.close(this.closingParams);
  }
}

//Taken from https://github.com/angular/components/blob/13.3.x/src/material/dialog/dialog-content-directives.ts
/**
 * Finds the closest MatDialogRef to an element by looking at the DOM.
 * @param element Element relative to which to look for a dialog.
 * @param openDialogs References to the currently-open dialogs.
 */
const getClosestDialog = (element: ElementRef<HTMLElement>, openDialogs: MatDialogRef<any>[]) => {
  let parent: HTMLElement | null = element.nativeElement.parentElement;

  while (parent && !parent.classList.contains('mat-dialog-container')) {
    parent = parent.parentElement;
  }

  return parent ? openDialogs.find(dialog => dialog.id === parent.id) : null;
};
